import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';

// Styled components
const FormContainer = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    padding: 15px;
    border-radius: 6px;
    margin-bottom: 15px;
  }
`;

const FormTitle = styled.h3`
  color: ${props => props.theme.text};
  margin-bottom: 15px;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  color: ${props => props.theme.text};
  font-weight: bold;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-size: 14px;
  
  @media (max-width: 768px) {
    padding: 12px;
    font-size: 16px;
    border-radius: 6px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-size: 14px;
  
  @media (max-width: 768px) {
    padding: 12px;
    font-size: 16px;
    border-radius: 6px;
    min-height: 100px;
  }
  resize: vertical;
`;

const Button = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-right: 10px;
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    margin: 10px 0;
    padding: 14px 20px;
    font-size: 16px;
    min-height: 48px;
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 10px;
  border-radius: 4px;
  margin-top: 15px;
  background: ${props => {
    if (props.type === 'success') return '#4CAF5020';
    if (props.type === 'error') return '#f4433620';
    return '#ff980020';
  }};
  border: 1px solid ${props => {
    if (props.type === 'success') return '#4CAF50';
    if (props.type === 'error') return '#f44336';
    return '#ff9800';
  }};
  color: ${props => props.theme.text};
`;

const ExtractedData = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  padding: 15px;
  margin-top: 15px;
`;

const ComparisonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 15px;
  margin-top: 20px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
    
    .raw-column {
      grid-column: 1 / -1;
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ComparisonPanel = styled.div`
  background: #2d3748;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 15px;
  color: #e2e8f0;
  
  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 6px;
    margin-bottom: 15px;
  }`;

const PanelHeader = styled.h4`
  margin: 0 0 15px 0;
  color: #f7fafc;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 2px solid #4a5568;
  padding-bottom: 8px;
`;

const DataField = styled.div`
  margin-bottom: 12px;
  
  strong {
    color: #63b3ed;
    font-weight: 600;
  }
`;

const GrokField = styled.div`
  margin-bottom: 12px;
  padding: 10px;
  background: #1a202c;
  border-radius: 6px;
  border-left: 4px solid #4CAF50;
  border: 1px solid #4a5568;
  
  .label {
    font-weight: bold;
    color: #68d391;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .value {
    margin-top: 6px;
    font-family: 'Consolas', 'Monaco', monospace;
    color: #f7fafc;
    font-size: 14px;
    line-height: 1.4;
  }
  
  .confidence {
    font-size: 11px;
    color: #a0aec0;
    margin-top: 4px;
    font-style: italic;
  }
`;

interface ExtractedTweet {
  // Raw tweet data
  tweet_id: string;
  tweet_text: string;
  tweet_url: string;
  tweet_created_at: string;
  tweet_metrics: {
    like_count?: number;
    retweet_count?: number;
    reply_count?: number;
  };
  
  // Author information
  expert_name: string;
  twitter_handle: string;
  author_verified: boolean;
  
  // Prediction data from Grok AI
  has_prediction?: boolean;
  price_prediction?: string;
  prediction_type?: string;
  target_time?: string;
  prediction_start_date?: string;
  price_min?: string;
  price_max?: string;
  confidence_level?: string;
  grok_interpretation_confidence?: string;
  prediction_notes?: string;
  
  // Grok self-assessment fields
  accuracy_confidence?: string;
  accuracy_reasoning?: string;
  interpretation_challenges?: string;
  data_completeness?: string;
  verification_status?: string;
  
  // Database fields for complete prediction submission
  prediction_made?: string; // DATE when prediction was made
  asset_type?: string; // 'bitcoin' or 'mstr'
  submitter_email?: string;
  notes?: string;
  source_url?: string;
  status?: string; // 'pending' | 'success' | 'miss'
  
  // Social media content
  tweet_proposals?: Array<{
    style: string;
    text: string;
  }>;
  
  // Processing metadata
  grok_used?: boolean;
  grok_cooldown_minutes?: number;
  extraction_timestamp?: string;
  source_tweet_text?: string; // Original tweet text for quote preservation
  profile_image_url?: string; // Profile image from tweet extraction
  
  // Tweet flags
  has_media?: boolean;
  is_retweet?: boolean;
  is_quote_tweet?: boolean;
  is_reply?: boolean;
  
  // Legacy fields for backward compatibility
  text?: string;
  author?: string;
  date?: string;
  url?: string;
}

const TweetSubmissionForm: React.FC = () => {
  const [tweetUrl, setTweetUrl] = useState('');
  const [extractedData, setExtractedData] = useState<ExtractedTweet | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
  const [editableData, setEditableData] = useState<any>({});

  const extractTweetData = async () => {
    if (!tweetUrl.trim()) {
      setStatus({ type: 'error', message: 'Please enter a tweet URL' });
      return;
    }

    // Basic URL validation
    if (!tweetUrl.includes('twitter.com') && !tweetUrl.includes('x.com')) {
      setStatus({ type: 'error', message: 'Please enter a valid Twitter/X.com URL' });
      return;
    }

    setIsExtracting(true);
    setStatus({ type: 'info', message: 'Extracting tweet data...' });

    try {
      const response = await axios.post(`${apiBase}/api/extract-tweet`, {
        url: tweetUrl
      });

      if (response.data.success) {
        const extracted = response.data.data;
        console.log('✅ Tweet extraction successful!');
        console.log('🖼️ Profile image URL:', extracted?.profile_image_url);
        console.log('📊 Full extracted data:', extracted);
        setExtractedData(extracted);
        
        // Use Grok AI extracted data with all comprehensive database fields
        setEditableData({
          // Core predictor information
          expert_name: extracted.expert_name || extracted.author?.name || '',
          twitter_handle: extracted.twitter_handle || '',
          
          // Prediction specifics
          prediction_type: extracted.prediction_type || 'target',
          price_prediction: extracted.price_prediction || '',
          price_min: extracted.price_min || '',
          price_max: extracted.price_max || '',
          
          // Timeline - IMPORTANT: Use actual tweet date for target_time
          target_time: extracted.tweet_created_at ? 
            new Date(extracted.tweet_created_at).toISOString().split('T')[0] : 
            (extracted.target_time || new Date().toISOString().split('T')[0]),
          tweet_created_at: extracted.tweet_created_at || null, // Store actual tweet timestamp
          tweet_id: extracted.tweet_id || null, // Store tweet ID for date extraction
          
          // Metadata & validation
          source_url: tweetUrl,
          asset_type: 'bitcoin', // Default to Bitcoin
          confidence_level: extracted.confidence_level || 'medium',
          status: 'pending', // Default status
          
          // STORE ALL EXTRACTED DATA - no re-extraction needed
          profile_image_url: extracted.profile_image_url || null, // Profile image URL
          author_verified: extracted.author_verified || false,
          prediction_notes: extracted.prediction_notes || extracted.notes || '',
          
          // Store complete extraction metadata for passing through
          extraction_complete: true,
          extraction_method: extracted.grok_used ? 'grok_ai' : 'text_parsing',
          extraction_timestamp: new Date().toISOString(),
          
          // Price data pre-extracted
          price_extracted: extracted.price_prediction || null,
          
          // Original tweet preservation
          original_tweet_text: extracted.tweet_text || extracted.source_tweet_text || '',
          prediction_made: new Date().toISOString().split('T')[0], // Default to today
          prediction_start_date: extracted.prediction_start_date || new Date().toISOString().split('T')[0],
          
          // Submission info
          submitter_email: '',
          notes: extracted.prediction_notes || '',
          
          // Internal tracking
          grok_used: extracted.grok_used || false,
          has_prediction: extracted.has_prediction !== false
        });
        setStatus({ type: 'success', message: 'Tweet data extracted successfully!' });
      } else {
        setStatus({ type: 'error', message: response.data.error || 'Failed to extract tweet data' });
      }
    } catch (error: any) {
      console.error('Error extracting tweet:', error);
      setStatus({ 
        type: 'error', 
        message: error.response?.data?.error || 'Failed to extract tweet data. This feature may require API keys.' 
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const submitToQueue = async () => {
    // Comprehensive validation for required fields
    const requiredFields: string[] = [];
    if (!editableData.expert_name) requiredFields.push('Expert Name');
    if (!editableData.price_prediction) requiredFields.push('Price Prediction');
    if (!editableData.prediction_type) requiredFields.push('Prediction Type');
    if (!editableData.target_time) requiredFields.push('Target Date');
    
    // Validate prediction type specific requirements
    if ((editableData.prediction_type === 'range' || editableData.prediction_type === 'pricetimerange') && !editableData.price_min) {
      requiredFields.push('Price Minimum (required for range/pricetimerange predictions)');
    }
    if (editableData.prediction_type === 'range' && !editableData.price_max) {
      requiredFields.push('Price Maximum (required for range predictions)');
    }

    if (requiredFields.length > 0) {
      setStatus({ 
        type: 'error', 
        message: `Please fill in required fields: ${requiredFields.join(', ')}` 
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: 'info', message: 'Submitting prediction for review...' });

    try {
      // Prepare submission data matching the backend expectations
      const submissionData = {
        // Core fields (backend expects these exact field names)
        expert_name: editableData.expert_name,
        twitter_handle: editableData.twitter_handle || extractedData?.twitter_handle || '',
        prediction_type: editableData.prediction_type,
        price_prediction: editableData.price_prediction,
        target_time: editableData.target_time,
        
        // COMPLETE EXTRACTION DATA - No re-extraction needed by backend!
        profile_image_url: extractedData?.profile_image_url || null,
        tweet_created_at: extractedData?.tweet_created_at || null,
        tweet_id: extractedData?.tweet_id || null,
        original_tweet_text: extractedData?.tweet_text || extractedData?.source_tweet_text || '',
        
        // Pre-extracted metadata to skip backend re-extraction
        extraction_complete: true,
        extraction_method: extractedData?.grok_used ? 'grok_ai' : 'text_parsing',
        extraction_timestamp: new Date().toISOString(),
        author_verified: extractedData?.author_verified || false,
        
        // Price range fields (properly formatted as numbers)
        price_min: editableData.price_min ? parseFloat(String(editableData.price_min).replace(/,/g, '')) : null,
        price_max: editableData.price_max ? parseFloat(String(editableData.price_max).replace(/,/g, '')) : null,
        
        // Timeline fields
        prediction_made: editableData.prediction_made || new Date().toISOString().split('T')[0],
        
        // Source and metadata
        source_url: editableData.source_url || extractedData?.tweet_url || tweetUrl,
        notes: editableData.notes || '',
        
        // Backend looks for 'email' or 'submitter_email'
        email: editableData.submitter_email || 'tweet-extractor@proofofprice.com',
        submitter_email: editableData.submitter_email || 'tweet-extractor@proofofprice.com',
        
        // Status and workflow
        status: editableData.status || 'pending',
        
        // Include AI analysis metadata for future integration (always save when we have extracted data)
        ai_metadata: extractedData ? JSON.stringify({
          tweet_proposals: extractedData.tweet_proposals || [],
          accuracy_confidence: extractedData.accuracy_confidence || 'medium',
          accuracy_reasoning: extractedData.accuracy_reasoning || 'Basic text extraction',
          interpretation_challenges: extractedData.interpretation_challenges || null,
          verification_status: extractedData.verification_status || 'unverified',
          grok_used: extractedData.grok_used || false,
          extraction_method: extractedData.grok_used ? 'grok_ai' : 'text_parsing',
          source_tweet_text: extractedData.source_tweet_text || null, // Original tweet text for quote preservation
          profile_image_url: extractedData.profile_image_url || null,
          twitter_handle: extractedData.twitter_handle || null
        }) : null
      };

      // Determine correct API endpoint based on asset type
      const assetType = editableData.asset_type || 'bitcoin';
      const apiEndpoint = assetType === 'mstr' ? '/api/mstr/submissions' : '/api/submissions';


      const response = await axios.post(`${apiBase}${apiEndpoint}`, submissionData);

      if (response.data.success) {
        setStatus({ 
          type: 'success', 
          message: `✅ Prediction submitted successfully! It will be reviewed and approved before going live. Submission ID: ${response.data.submission?.id || 'N/A'}` 
        });
        
        // Reset form after successful submission
        setTimeout(() => {
          setTweetUrl('');
          setExtractedData(null);
          setEditableData({});
          setStatus(null);
        }, 3000);
      } else {
        setStatus({ type: 'error', message: response.data.message || 'Failed to submit prediction' });
      }
    } catch (error: any) {
      console.error('Error submitting prediction:', error);
      
      let errorMessage = 'Failed to submit prediction';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setStatus({ 
        type: 'error', 
        message: errorMessage 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer>
      <FormTitle>🐦 Extract Tweet Predictions</FormTitle>
      
      <FormGroup>
        <Label>Tweet URL:</Label>
        <Input
          type="url"
          value={tweetUrl}
          onChange={(e) => setTweetUrl(e.target.value)}
          placeholder="https://twitter.com/username/status/1234567890 or https://x.com/username/status/1234567890"
        />
      </FormGroup>

      <Button 
        onClick={extractTweetData} 
        disabled={isExtracting || !tweetUrl.trim()}
      >
        {isExtracting ? '⏳ Extracting...' : '🔍 Extract Tweet Data'}
      </Button>

      {status && (
        <StatusMessage type={status.type}>
          {status.message}
        </StatusMessage>
      )}

      {extractedData && (
        <>
          <ComparisonContainer>
            {/* Left Panel: Raw Tweet Extraction */}
            <ComparisonPanel className="raw-column" style={{ borderColor: '#9C27B0' }}>
              <PanelHeader>
                📱 Raw Tweet Data
              </PanelHeader>

              <DataField><strong>Tweet ID:</strong> {extractedData.tweet_id}</DataField>
              <DataField><strong>Author Name:</strong> {extractedData.expert_name}</DataField>
              <DataField><strong>Username:</strong> {extractedData.twitter_handle}</DataField>
              
              {/* Profile Image Display */}
              {extractedData.profile_image_url && (
                <DataField style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <strong>Profile Image:</strong>
                  <img 
                    src={extractedData.profile_image_url} 
                    alt={`${extractedData.expert_name} profile`}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      border: '2px solid #4a5568',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      console.error('Profile image failed to load:', extractedData.profile_image_url);
                      // Hide image if it fails to load
                      e.currentTarget.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('Profile image loaded successfully:', extractedData.profile_image_url);
                    }}
                  />
                  <span style={{ fontSize: '12px', color: '#a0aec0' }}>✓ Image pulled from X API</span>
                  <br />
                  <small style={{ fontSize: '10px', color: '#666', wordBreak: 'break-all' }}>
                    {extractedData.profile_image_url}
                  </small>
                </DataField>
              )}
              {!extractedData.profile_image_url && (
                <DataField style={{ color: '#ff9800' }}>
                  <strong>Profile Image:</strong> Not available
                </DataField>
              )}
              
              <DataField><strong>Verified:</strong> {extractedData.author_verified ? '✓ Yes' : '✗ No'}</DataField>
              <DataField><strong>Posted:</strong> {new Date(extractedData.tweet_created_at).toLocaleString()}</DataField>
              <DataField><strong>Engagement:</strong></DataField>
              <div style={{ marginLeft: '15px', fontSize: '13px', color: '#a0aec0' }}>
                <div>👍 <span style={{ color: '#68d391' }}>{extractedData.tweet_metrics?.like_count?.toLocaleString() || '0'}</span> likes</div>
                <div>🔄 <span style={{ color: '#68d391' }}>{extractedData.tweet_metrics?.retweet_count?.toLocaleString() || '0'}</span> retweets</div>
                <div>💬 <span style={{ color: '#68d391' }}>{extractedData.tweet_metrics?.reply_count?.toLocaleString() || '0'}</span> replies</div>
              </div>
              
              <DataField style={{ marginTop: '15px' }}>
                <strong>Tweet Content:</strong>
                <div style={{ 
                  background: '#1a202c', 
                  padding: '12px', 
                  borderRadius: '6px', 
                  marginTop: '8px',
                  fontStyle: 'italic',
                  border: '1px solid #4a5568',
                  color: '#e2e8f0',
                  lineHeight: '1.5'
                }}>
                  "{extractedData.tweet_text}"
                </div>
              </DataField>

              {extractedData.has_media && (
                <DataField style={{ color: '#2196F3' }}>
                  <strong>📸 Media:</strong> Contains images/attachments
                </DataField>
              )}

              {extractedData.is_retweet && (
                <DataField style={{ color: '#ff9800' }}>
                  <strong>🔄 Retweet:</strong> This is a retweet
                </DataField>
              )}
            </ComparisonPanel>

            {/* Middle Panel: Grok AI Analysis */}
            <ComparisonPanel style={{ borderColor: '#4CAF50' }}>
              <PanelHeader>
                🤖 Grok AI Analysis
                {!extractedData.grok_used && <span style={{ fontSize: '12px', color: '#ff9800' }}>(Basic parsing used)</span>}
              </PanelHeader>

              <GrokField>
                <div className="label">Expert Name</div>
                <div className="value" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {extractedData.expert_name || 'Not detected'}
                  {extractedData.profile_image_url && (
                    <img 
                      src={extractedData.profile_image_url} 
                      alt={`${extractedData.expert_name} profile`}
                      style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        border: '1px solid #4CAF50',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        console.error('Grok panel: Profile image failed to load:', extractedData.profile_image_url);
                        e.currentTarget.style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log('Grok panel: Profile image loaded successfully:', extractedData.profile_image_url);
                      }}
                    />
                  )}
                  {!extractedData.profile_image_url && (
                    <span style={{ fontSize: '10px', color: '#ff9800' }}>(No image)</span>
                  )}
                </div>
              </GrokField>

              <GrokField>
                <div className="label">Twitter Handle</div>
                <div className="value">{extractedData.twitter_handle || 'Not detected'}</div>
              </GrokField>

              <GrokField>
                <div className="label">Price Prediction</div>
                <div className="value">{extractedData.price_prediction || 'No prediction found'}</div>
              </GrokField>

              <GrokField>
                <div className="label">Prediction Type</div>
                <div className="value">{extractedData.prediction_type || 'Unknown'}</div>
                <div className="confidence">
                  {extractedData.prediction_type === 'bottom' && '💪 Price floor - will never go below'}
                  {extractedData.prediction_type === 'target' && '🎯 Direct price goal'}
                  {extractedData.prediction_type === 'top' && '🔝 Price ceiling - won\'t exceed'}
                  {extractedData.prediction_type === 'range' && '📊 Between minimum and maximum'}
                  {extractedData.prediction_type === 'pricetimerange' && '⏰ Price + specific timeframe'}
                </div>
              </GrokField>

              <GrokField>
                <div className="label">Target Time</div>
                <div className="value">{extractedData.target_time || 'No timeframe specified'}</div>
              </GrokField>

              {extractedData.price_min && (
                <GrokField>
                  <div className="label">Price Minimum</div>
                  <div className="value">{extractedData.price_min}</div>
                </GrokField>
              )}

              {extractedData.price_max && (
                <GrokField>
                  <div className="label">Price Maximum</div>
                  <div className="value">{extractedData.price_max}</div>
                </GrokField>
              )}

              <GrokField>
                <div className="label">Prediction Confidence</div>
                <div className="value">{extractedData.confidence_level || 'medium'}</div>
                <div className="confidence">
                  {extractedData.confidence_level === 'high' && '🔥 High confidence in prediction'}
                  {extractedData.confidence_level === 'medium' && '👍 Medium confidence in prediction'}
                  {extractedData.confidence_level === 'low' && '🤔 Low confidence in prediction'}
                </div>
              </GrokField>

              <GrokField style={{ border: '2px solid #ff9800' }}>
                <div className="label">🤖 Grok's Interpretation Accuracy</div>
                <div className="value">{extractedData.grok_interpretation_confidence || 'medium'}</div>
                <div className="confidence">
                  {extractedData.grok_interpretation_confidence === 'high' && '✅ Grok is very confident about this analysis'}
                  {extractedData.grok_interpretation_confidence === 'medium' && '⚠️ Grok has moderate confidence - verify details'}
                  {extractedData.grok_interpretation_confidence === 'low' && '❌ Grok is uncertain - manual review needed'}
                </div>
              </GrokField>

              {/* New Self-Assessment Fields */}
              <GrokField style={{ border: '2px solid #9c27b0' }}>
                <div className="label">🎯 Grok's Accuracy Self-Assessment</div>
                <div className="value">{extractedData.accuracy_confidence || 'medium'}</div>
                <div className="confidence">
                  {extractedData.accuracy_confidence === 'high' && '🎯 Grok believes this interpretation is highly accurate'}
                  {extractedData.accuracy_confidence === 'medium' && '📊 Grok thinks this interpretation is reasonably accurate'}
                  {extractedData.accuracy_confidence === 'low' && '⚠️ Grok has concerns about interpretation accuracy'}
                </div>
              </GrokField>

              {extractedData.accuracy_reasoning && (
                <GrokField style={{ border: '1px solid #7b1fa2' }}>
                  <div className="label">🧠 Grok's Reasoning</div>
                  <div className="value" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                    {extractedData.accuracy_reasoning}
                  </div>
                </GrokField>
              )}

              {extractedData.interpretation_challenges && (
                <GrokField style={{ border: '1px solid #f57c00' }}>
                  <div className="label">🚧 Analysis Challenges</div>
                  <div className="value" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                    {extractedData.interpretation_challenges}
                  </div>
                </GrokField>
              )}

              <GrokField style={{ border: '1px solid #388e3c' }}>
                <div className="label">📊 Data Completeness</div>
                <div className="value">{extractedData.data_completeness || 'complete'}</div>
                <div className="confidence">
                  {extractedData.data_completeness === 'complete' && '✅ All relevant data extracted successfully'}
                  {extractedData.data_completeness === 'partial' && '⚠️ Some data may be missing or incomplete'}
                  {extractedData.data_completeness === 'limited' && '❌ Limited data available for analysis'}
                </div>
              </GrokField>

              <GrokField style={{ border: '1px solid #1976d2' }}>
                <div className="label">🔍 Verification Status</div>
                <div className="value">{extractedData.verification_status || 'unverified'}</div>
                <div className="confidence">
                  {extractedData.verification_status === 'verified' && '✅ Account and prediction appear credible'}
                  {extractedData.verification_status === 'unverified' && '⚠️ Unable to verify account credibility'}
                  {extractedData.verification_status === 'suspicious' && '🚨 Potential issues with account or prediction'}
                </div>
              </GrokField>

              {extractedData.prediction_notes && (
                <GrokField>
                  <div className="label">Grok's Analysis Notes</div>
                  <div className="value" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                    {extractedData.prediction_notes}
                  </div>
                </GrokField>
              )}
            </ComparisonPanel>

            {/* Right Panel: Your Final Decision */}
            <ComparisonPanel style={{ borderColor: '#2196F3' }}>
              <PanelHeader>
                ✏️ Your Final Decision - Complete Prediction Data
              </PanelHeader>
              
              {/* CORE IDENTIFICATION */}
              <div style={{ marginBottom: '15px', padding: '10px', background: '#1a202c', borderRadius: '6px', border: '2px solid #2196F3' }}>
                <h5 style={{ margin: '0 0 10px 0', color: '#64b5f6', fontSize: '14px' }}>👤 PREDICTOR INFORMATION</h5>
                
                <FormGroup>
                  <Label>Expert Name: *</Label>
                  <Input
                    value={editableData.expert_name || ''}
                    onChange={(e) => setEditableData({...editableData, expert_name: e.target.value})}
                    placeholder="e.g., Michael Saylor, Cathie Wood"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Twitter Handle:</Label>
                  <Input
                    value={editableData.twitter_handle || ''}
                    onChange={(e) => setEditableData({...editableData, twitter_handle: e.target.value})}
                    placeholder="@username (with @)"
                  />
                </FormGroup>
              </div>

              {/* PREDICTION DETAILS */}
              <div style={{ marginBottom: '15px', padding: '10px', background: '#1a202c', borderRadius: '6px', border: '2px solid #4caf50' }}>
                <h5 style={{ margin: '0 0 10px 0', color: '#81c784', fontSize: '14px' }}>🎯 PREDICTION SPECIFICS</h5>
                
                <FormGroup>
                  <Label>Prediction Type: *</Label>
                  <select
                    value={editableData.prediction_type || 'target'}
                    onChange={(e) => setEditableData({...editableData, prediction_type: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #4a5568',
                      borderRadius: '4px',
                      background: '#2d3748',
                      color: '#e2e8f0'
                    }}
                    required
                  >
                    <option value="target">Target - Price will reach X</option>
                    <option value="bottom">Bottom - Price won't go below X</option>
                    <option value="top">Top - Price won't go above X</option>
                    <option value="range">Range - Price between X and Y</option>
                    <option value="pricetimerange">Price Time Range - Above X by time Y</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <Label>Price Prediction (formatted): *</Label>
                  <Input
                    value={editableData.price_prediction || ''}
                    onChange={(e) => setEditableData({...editableData, price_prediction: e.target.value})}
                    placeholder="$100,000 or $80k-120k or Above $50,000"
                    required
                  />
                </FormGroup>

                {(editableData.prediction_type === 'range' || editableData.prediction_type === 'pricetimerange') && (
                  <>
                    <FormGroup>
                      <Label>Price Minimum (numeric): *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editableData.price_min || ''}
                        onChange={(e) => setEditableData({...editableData, price_min: e.target.value})}
                        placeholder="80000"
                      />
                    </FormGroup>
                    
                    {editableData.prediction_type === 'range' && (
                      <FormGroup>
                        <Label>Price Maximum (numeric): *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editableData.price_max || ''}
                          onChange={(e) => setEditableData({...editableData, price_max: e.target.value})}
                          placeholder="120000"
                        />
                      </FormGroup>
                    )}
                  </>
                )}
              </div>

              {/* TIMELINE */}
              <div style={{ marginBottom: '15px', padding: '10px', background: '#1a202c', borderRadius: '6px', border: '2px solid #ff9800' }}>
                <h5 style={{ margin: '0 0 10px 0', color: '#ffb74d', fontSize: '14px' }}>⏰ TIMELINE</h5>
                
                <FormGroup>
                  <Label>Target Date (when prediction expires): *</Label>
                  <Input
                    type="date"
                    value={editableData.target_time || ''}
                    onChange={(e) => setEditableData({...editableData, target_time: e.target.value})}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Prediction Made Date (when was this prediction made):</Label>
                  <Input
                    type="date"
                    value={editableData.prediction_made || ''}
                    onChange={(e) => setEditableData({...editableData, prediction_made: e.target.value})}
                    placeholder="Date the prediction was originally made"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Start Date (when prediction becomes active):</Label>
                  <Input
                    type="date"
                    value={editableData.prediction_start_date || ''}
                    onChange={(e) => setEditableData({...editableData, prediction_start_date: e.target.value})}
                  />
                </FormGroup>
              </div>

              {/* METADATA */}
              <div style={{ marginBottom: '15px', padding: '10px', background: '#1a202c', borderRadius: '6px', border: '2px solid #9c27b0' }}>
                <h5 style={{ margin: '0 0 10px 0', color: '#ba68c8', fontSize: '14px' }}>📊 METADATA & VALIDATION</h5>
                
                <FormGroup>
                  <Label>Source URL: *</Label>
                  <Input
                    type="url"
                    value={editableData.source_url || extractedData.tweet_url || ''}
                    onChange={(e) => setEditableData({...editableData, source_url: e.target.value})}
                    placeholder="https://x.com/user/status/123..."
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Asset Type:</Label>
                  <select
                    value={editableData.asset_type || 'bitcoin'}
                    onChange={(e) => setEditableData({...editableData, asset_type: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #4a5568',
                      borderRadius: '4px',
                      background: '#2d3748',
                      color: '#e2e8f0'
                    }}
                  >
                    <option value="bitcoin">Bitcoin (BTC)</option>
                    <option value="mstr">MicroStrategy (MSTR)</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <Label>Confidence Level:</Label>
                  <select
                    value={editableData.confidence_level || 'medium'}
                    onChange={(e) => setEditableData({...editableData, confidence_level: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #4a5568',
                      borderRadius: '4px',
                      background: '#2d3748',
                      color: '#e2e8f0'
                    }}
                  >
                    <option value="low">Low - Vague or speculative</option>
                    <option value="medium">Medium - Some specifics</option>
                    <option value="high">High - Very specific and credible</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <Label>Prediction Status:</Label>
                  <select
                    value={editableData.status || 'pending'}
                    onChange={(e) => setEditableData({...editableData, status: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #4a5568',
                      borderRadius: '4px',
                      background: '#2d3748',
                      color: '#e2e8f0'
                    }}
                  >
                    <option value="pending">Pending - Awaiting evaluation</option>
                    <option value="success">Success - Prediction achieved</option>
                    <option value="miss">Miss - Prediction failed</option>
                  </select>
                </FormGroup>
              </div>

              {/* SUBMISSION INFO */}
              <div style={{ marginBottom: '15px', padding: '10px', background: '#1a202c', borderRadius: '6px', border: '2px solid #607d8b' }}>
                <h5 style={{ margin: '0 0 10px 0', color: '#90a4ae', fontSize: '14px' }}>📧 SUBMISSION INFO</h5>
                
                <FormGroup>
                  <Label>Your Email (for follow-up):</Label>
                  <Input
                    type="email"
                    value={editableData.submitter_email || ''}
                    onChange={(e) => setEditableData({...editableData, submitter_email: e.target.value})}
                    placeholder="your.email@example.com"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Notes & Context:</Label>
                  <TextArea
                    value={editableData.notes || ''}
                    onChange={(e) => setEditableData({...editableData, notes: e.target.value})}
                    placeholder="Additional context, conditions, or important details about this prediction..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #4a5568',
                      borderRadius: '4px',
                      background: '#2d3748',
                      color: '#e2e8f0',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </FormGroup>
              </div>

              {/* AI ANALYSIS STATUS */}
              {extractedData?.grok_used && (
                <div style={{ background: '#4CAF5010', padding: '10px', borderRadius: '6px', border: '1px solid #4CAF50', marginBottom: '10px' }}>
                  <strong style={{ color: '#4caf50' }}>🤖 Grok AI Analysis Status:</strong>
                  <br />
                  <small style={{ color: '#81c784' }}>
                    ✅ Used for intelligent prediction interpretation<br />
                    🎯 Interpretation Confidence: {extractedData?.grok_interpretation_confidence || 'medium'}<br />
                    📊 Accuracy Self-Assessment: {extractedData?.accuracy_confidence || 'medium'}
                  </small>
                </div>
              )}
            </ComparisonPanel>
          </ComparisonContainer>

          {/* Tweet Proposals Section */}
          {extractedData?.tweet_proposals && extractedData.tweet_proposals.length > 0 && (
            <div style={{ marginTop: '25px', marginBottom: '20px' }}>
              <div style={{ 
                background: '#1a202c', 
                border: '2px solid #00bcd4', 
                borderRadius: '8px', 
                padding: '20px',
                color: '#e2e8f0'
              }}>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#00bcd4', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  fontSize: '18px'
                }}>
                  🐦 Ready-to-Post Tweet Proposals by Grok AI
                </h3>
                
                <p style={{ 
                  margin: '0 0 20px 0', 
                  color: '#a0aec0', 
                  fontSize: '14px' 
                }}>
                  Copy and paste these engaging tweets to promote this prediction on social media:
                </p>

                {extractedData.tweet_proposals.map((proposal, index) => (
                  <div 
                    key={index}
                    style={{ 
                      marginBottom: '15px',
                      padding: '15px',
                      background: '#2d3748',
                      borderRadius: '8px',
                      border: '1px solid #4a5568'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '10px'
                    }}>
                      <span style={{ 
                        background: '#00bcd4',
                        color: '#000',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                      }}>
                        {proposal.style} Style
                      </span>
                      <span style={{ 
                        color: '#a0aec0',
                        fontSize: '12px'
                      }}>
                        {proposal.text.length}/280 chars
                      </span>
                    </div>
                    
                    <div style={{ 
                      background: '#1a202c',
                      border: '1px solid #4a5568',
                      borderRadius: '6px',
                      padding: '12px',
                      fontFamily: 'Twitter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto',
                      fontSize: '15px',
                      lineHeight: '1.4',
                      color: '#f7fafc',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {proposal.text}
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      gap: '10px', 
                      marginTop: '10px' 
                    }}>
                      <button
                        onClick={() => navigator.clipboard.writeText(proposal.text)}
                        style={{
                          background: '#00bcd4',
                          color: '#000',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        📋 Copy Tweet
                      </button>
                      
                      <button
                        onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(proposal.text)}`, '_blank')}
                        style={{
                          background: '#1da1f2',
                          color: '#fff',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        🐦 Post on X
                      </button>
                    </div>
                  </div>
                ))}
                
                <div style={{ 
                  marginTop: '15px',
                  padding: '10px',
                  background: '#2d3748',
                  borderRadius: '6px',
                  border: '1px solid #4a5568'
                }}>
                  <p style={{ 
                    margin: '0',
                    color: '#a0aec0',
                    fontSize: '12px',
                    fontStyle: 'italic'
                  }}>
                    💡 Tip: These tweets are generated by Grok AI based on the prediction data. 
                    Feel free to customize them before posting to match your brand voice!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button Outside Comparison */}
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Button 
              onClick={submitToQueue} 
              disabled={isSubmitting}
              style={{ fontSize: '16px', padding: '12px 24px' }}
            >
              {isSubmitting ? '⏳ Submitting for Review...' : '� Submit Prediction for Admin Review'}
            </Button>
          </div>
        </>
      )}
    </FormContainer>
  );
};

export default TweetSubmissionForm;